import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, Play, CheckCircle, XCircle, Printer } from 'lucide-react';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';
import { toast } from 'sonner';

// Document-control values printed on the indent form. These belong to the
// controlled format itself (not to the order) — set EFFECTIVE_DATE once the
// approved effective date of WH003-F02-02 is known; blank prints a fill-in space.
const EFFECTIVE_DATE = '';
const FORMAT_NO = 'WH003-F02-02';

const ProductionOrders = () => {
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [boms, setBoms] = useState([]);
  const [routings, setRoutings] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const emptyForm = { product_sku: '', bom_id: '', routing_id: '', planned_qty: 0, plant_id: '', shift_id: '', planned_start: '', planned_end: '', priority: 'normal', output_type: 'pouching', batch_number: '', customer_name: '', notes: '' };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [oRes, iRes, bRes, rRes, sRes, pRes] = await Promise.all([
        api.get('/production/orders'), api.get('/sales/inventory'), api.get('/production/bom'),
        api.get('/production/routing'), api.get('/sales/shifts'), api.get('/sales/plants')
      ]);
      setOrders(oRes.data); setInventory(iRes.data); setBoms(bRes.data);
      setRoutings(rRes.data); setShifts(sRes.data); setPlants(pRes.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };
  const openEdit = (o) => {
    setEditing(o);
    setFormData({
      product_sku: o.product_sku, bom_id: o.bom_id || '', routing_id: o.routing_id || '',
      planned_qty: o.planned_qty, plant_id: o.plant_id || '', shift_id: o.shift_id || '',
      planned_start: o.planned_start ? o.planned_start.substring(0, 16) : '',
      planned_end: o.planned_end ? o.planned_end.substring(0, 16) : '',
      priority: o.priority || 'normal', notes: o.notes || '',
      // These print on the indent form — load them so an edit doesn't show blanks.
      output_type: o.output_type || 'pouching',
      batch_number: o.batch_number || '',
      customer_name: o.customer_name || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/production/orders/${editing.id}`, formData);
      else await api.post('/production/orders', formData);
      setShowModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/production/orders/${id}`, { status }); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Delete order "${num}"?`)) return;
    try { await api.delete(`/production/orders/${id}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ── Indent for Pouching (ANNEXURE-II, Format No. WH003-F02-02) ─────────────
  // Prints a production order on the client's warehouse indent form. Everything
  // the order knows is filled in; the issuance / sign-off rows are left blank
  // because they are completed and signed by hand on the shop floor.
  const fmtDate = (val) => {
    if (!val) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(val));
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(val);
    return isNaN(d) ? String(val) : d.toLocaleDateString('en-GB');
  };

  const printOrder = (o) => {
    const fg = inventory.find(i => i.sku === o.product_sku) || {};
    const bom = boms.find(b => b.id === o.bom_id);
    const components = bom?.components || [];
    const materialNameQty = components
      .map(c => `${c.name || c.sku || ''}${c.quantity ? ` – ${c.quantity} ${c.unit || ''}`.trimEnd() : ''}`)
      .join('<br/>');
    const materialSize = components
      .map(c => {
        const inv = inventory.find(i => i.sku === c.sku);
        return `${c.name || c.sku || ''}: ${inv?.size || ''}`;
      })
      .join('<br/>');
    const title = (o.output_type || 'pouching') === 'pouching'
      ? 'Indent for Pouching'
      : `Indent for ${(o.output_type || '').replace('_', ' ').replace(/\b\w/g, ch => ch.toUpperCase())}`;
    // A value cell that stays tall enough to write in when we have nothing to print.
    const fill = (v) => `<span class="val">${v || ''}</span>`;

    const w = window.open('', `prod_order_print_${o.id}`, 'width=900,height=760');
    w.document.open();
    w.document.write(`<html><head><title>${title} - ${o.order_number}</title>
      <style>
        @page{size:A4;margin:12mm}
        body{font-family:Arial,sans-serif;font-size:12px;color:#000;padding:6px}
        table{width:100%;border-collapse:collapse}
        td{border:1px solid #000;padding:6px 8px;font-size:12px;vertical-align:top}
        .doc-head td{padding:6px 8px}
        .doc-head .brand{width:34%;text-align:center;font-weight:bold}
        .doc-head .brand img{height:42px;width:auto;display:block;margin:0 auto 2px}
        .sec td{background:#e8e8e8;font-weight:bold;text-align:center}
        .lbl{font-weight:bold}
        .val{font-weight:normal}
        .sign td{height:44px}
        .blank td{height:26px}
        .note{font-weight:normal;font-size:11px}
        .foot{margin-top:10px;display:flex;justify-content:space-between;font-size:11px}
        @media print{button{display:none !important}}
      </style></head><body>

      <table class="doc-head">
        <tr>
          <td class="brand" rowspan="2">
            <img src="https://hridya.dweb.co.in/hridayalogo.png" alt="Hridya Pack" />
            HRIDYA PACK
          </td>
          <td style="width:33%;text-align:center;font-weight:bold">ANNEXURE-II</td>
          <td style="width:33%"><span class="lbl">Effective Date:</span> ${EFFECTIVE_DATE}</td>
        </tr>
        <tr>
          <td colspan="2"><span class="lbl">Title:</span> ${title}</td>
        </tr>
      </table>

      <table style="margin-top:-1px">
        <tr class="sec"><td colspan="4">Materials required for production</td></tr>
        <tr><td colspan="4"><span class="lbl">Batch Number:</span> ${fill(o.batch_number)}</td></tr>
        <tr>
          <td colspan="2"><span class="lbl">Indent No.:</span> ${fill(o.order_number)}</td>
          <td colspan="2"><span class="lbl">Issued on.:</span> ${fill(fmtDate(o.planned_start || o.created_at))}</td>
        </tr>
        <tr><td colspan="4"><span class="lbl">Material Name and Quantity:</span><br/>${fill(materialNameQty)}</td></tr>
        <tr><td colspan="4"><span class="lbl">Material Size:</span><br/>${fill(materialSize)}</td></tr>

        <tr class="sec"><td colspan="4">Job Details</td></tr>
        <tr><td colspan="4"><span class="lbl">Job Name:</span> ${fill(o.product_name)}</td></tr>
        <tr><td colspan="4"><span class="lbl">Job Size:</span> ${fill(fg.size)}</td></tr>
        <tr><td colspan="4"><span class="lbl">Job Quantity:</span> ${fill(`${o.planned_qty || ''} ${fg.unit || ''}`.trim())}</td></tr>
        <tr><td colspan="4"><span class="lbl">Customer Name:</span> ${fill(o.customer_name)}</td></tr>

        <tr>
          <td colspan="2" style="text-align:center" class="lbl">Requested by (PD)</td>
          <td colspan="2" style="text-align:center" class="lbl">Received by (WH)</td>
        </tr>
        <tr class="sign">
          <td class="lbl">Sign &amp; Date</td><td></td>
          <td class="lbl">Sign &amp; Date</td><td></td>
        </tr>

        <tr class="blank">
          <td colspan="2"><span class="lbl">Issued from MRN:</span></td>
          <td colspan="2"><span class="lbl">Issued Quantity:</span></td>
        </tr>
        <tr class="blank">
          <td colspan="2"><span class="lbl">Issued by (WH):</span></td>
          <td colspan="2"><span class="lbl">Received by (PD):</span></td>
        </tr>
        <tr class="blank"><td colspan="4"><span class="lbl">Total quantity sent for sealing:</span></td></tr>
        <tr class="blank"><td colspan="4"><span class="lbl">Updated by (PD):</span></td></tr>

        <tr class="sec"><td colspan="4">Material re-issuance details
          <div class="note">(if previously issued quantity does not meet the Indent Quantity)</div></td></tr>
        <tr class="blank">
          <td colspan="2"><span class="lbl">Issued from MRN:</span></td>
          <td colspan="2"><span class="lbl">Issued Quantity:</span></td>
        </tr>
        <tr class="blank">
          <td colspan="2"><span class="lbl">Issued by (WH):</span></td>
          <td colspan="2"><span class="lbl">Received by (PD):</span></td>
        </tr>
        <tr class="blank"><td colspan="4"><span class="lbl">Total quantity sent for sealing (PD):</span></td></tr>
      </table>

      <div class="foot"><div>Format No.: ${FORMAT_NO}</div><div>Page 1 of 1</div></div>
      <button onclick="window.print()" style="margin-top:18px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const getStatusBadge = (s) => {
    const map = { draft: 'badge-info', planned: 'badge-warning', released: 'badge-warning', in_progress: 'badge-info', completed: 'badge-success', cancelled: 'badge-danger' };
    return `badge ${map[s] || 'badge-info'}`;
  };

  const getPriorityBadge = (p) => {
    const map = { high: 'badge-danger', normal: 'badge-info', low: 'badge-success' };
    return `badge ${map[p] || 'badge-info'}`;
  };

  const filtered = orders.filter(o => {
    const matchSearch = (o.order_number || '').toLowerCase().includes(searchTerm.toLowerCase()) || (o.product_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="production-orders-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Production Orders</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Planning Layer - Create and manage production orders</p>
        </div>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/production-orders" filename="production_orders_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/production-orders" filename="production_orders_export.pdf" />
          <button onClick={openCreate} className="btn btn-primary" data-testid="add-order-btn"><Plus size={16} /> New Production Order</button>
        </div>
      </div>

      <div className="grid grid-cols-4 mb-4">
        {['draft', 'planned', 'in_progress', 'completed'].map(st => {
          const count = orders.filter(o => o.status === st).length;
          const totalPlanned = orders.filter(o => o.status === st).reduce((s, o) => s + (o.planned_qty || 0), 0);
          return (
            <div key={st} className="stat-card" data-testid={`stat-${st}`}>
              <div className="stat-value">{count}</div>
              <div className="stat-label">{st.replace('_', ' ')} ({totalPlanned} qty)</div>
            </div>
          );
        })}
      </div>

      <div className="card mb-4">
        <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search orders..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
          <select className="form-select" style={{ width: '160px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="planned">Planned</option>
            <option value="released">Released</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Order #</th><th>Product</th><th>Output</th><th>Current Stage</th><th>Planned Qty</th><th>Actual</th><th>Priority</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No orders found</td></tr> : filtered.map(o => {
                  const stageColors = { printing: '#3b82f6', lamination: '#8b5cf6', slitting: '#f59e0b', converting: '#10b981', end_product: '#ef4444', completed: '#059669' };
                  const curStage = o.current_stage || o.status;
                  return (
                  <tr key={o.id} data-testid={`order-row-${o.order_number}`}>
                    <td className="font-mono" style={{ fontWeight: 500 }}>{o.order_number}</td>
                    <td><div>{o.product_name}</div><div className="text-xs text-muted">{o.product_sku}</div></td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{(o.output_type || 'pouching').replace('_', ' ')}</span></td>
                    <td><span className="badge" style={{ background: stageColors[curStage] || '#6b7280', color: '#fff', textTransform: 'capitalize' }}>{(curStage || 'draft').replace('_', ' ')}</span></td>
                    <td className="tabular-nums">{o.planned_qty}</td>
                    <td className="tabular-nums">{o.actual_qty || 0}</td>
                    <td><span className={getPriorityBadge(o.priority)}>{o.priority}</span></td>
                    <td><span className={getStatusBadge(o.status)}>{o.status?.replace('_', ' ')}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => printOrder(o)} className="btn btn-ghost btn-sm" title="Print Indent" data-testid={`print-btn-${o.order_number}`}><Printer size={14} /></button>
                        {o.status === 'draft' && <button onClick={() => updateStatus(o.id, 'planned')} className="btn btn-ghost btn-sm" title="Plan"><Play size={14} color="#0047AB" /></button>}
                        {o.status === 'planned' && <button onClick={() => updateStatus(o.id, 'in_progress')} className="btn btn-ghost btn-sm" title="Start Production"><Play size={14} color="#059669" /></button>}
                        {['draft', 'planned'].includes(o.status) && <button onClick={() => openEdit(o)} className="btn btn-ghost btn-sm"><Edit size={14} /></button>}
                        {['draft', 'cancelled'].includes(o.status) && <button onClick={() => handleDelete(o.id, o.order_number)} className="btn btn-ghost btn-sm"><Trash2 size={14} color="#DC2626" /></button>}
                        {!['completed', 'cancelled'].includes(o.status) && <button onClick={() => updateStatus(o.id, 'cancelled')} className="btn btn-ghost btn-sm" title="Cancel"><XCircle size={14} color="#DC2626" /></button>}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="order-modal">
          <div className="modal-content" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Production Order' : 'New Production Order'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Product (FG) *</label>
                    <select className="form-select" value={formData.product_sku} onChange={(e) => setFormData({...formData, product_sku: e.target.value})} required data-testid="product-select">
                      <option value="">Select Product</option>
                      {inventory.filter(i => i.product_category === 'fg').map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Planned Quantity *</label><input type="number" className="form-input" value={formData.planned_qty} onChange={(e) => setFormData({...formData, planned_qty: parseInt(e.target.value) || 0})} required min={1} data-testid="qty-input" /></div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">BOM</label>
                    <select className="form-select" value={formData.bom_id} onChange={(e) => setFormData({...formData, bom_id: e.target.value})} data-testid="bom-select">
                      <option value="">None</option>
                      {boms.filter(b => !formData.product_sku || b.product_sku === formData.product_sku).map(b => <option key={b.id} value={b.id}>{b.bom_code} (v{b.version})</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Routing</label>
                    <select className="form-select" value={formData.routing_id} onChange={(e) => setFormData({...formData, routing_id: e.target.value})} data-testid="routing-select">
                      <option value="">None</option>
                      {routings.filter(r => !formData.product_sku || r.product_sku === formData.product_sku).map(r => <option key={r.id} value={r.id}>{r.routing_code}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Plant</label>
                    <select className="form-select" value={formData.plant_id} onChange={(e) => setFormData({...formData, plant_id: e.target.value})} data-testid="plant-select">
                      <option value="">Select Plant</option>
                      {plants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Shift</label>
                    <select className="form-select" value={formData.shift_id} onChange={(e) => setFormData({...formData, shift_id: e.target.value})} data-testid="shift-select">
                      <option value="">Select Shift</option>
                      {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.start_time}-{s.end_time})</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Planned Start</label><input type="datetime-local" className="form-input" value={formData.planned_start} onChange={(e) => setFormData({...formData, planned_start: e.target.value})} data-testid="start-input" /></div>
                  <div className="form-group"><label className="form-label">Planned End</label><input type="datetime-local" className="form-input" value={formData.planned_end} onChange={(e) => setFormData({...formData, planned_end: e.target.value})} data-testid="end-input" /></div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select className="form-select" value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} data-testid="priority-select">
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Output Type *</label>
                    <select className="form-select" value={formData.output_type} onChange={(e) => setFormData({...formData, output_type: e.target.value})} required data-testid="output-type-select">
                      <option value="pouching">Pouching</option>
                      <option value="roll_form">Roll Form</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Batch Number</label><input type="text" className="form-input" value={formData.batch_number} onChange={(e) => setFormData({...formData, batch_number: e.target.value})} placeholder="e.g. HP-ALU-238-25" data-testid="batch-input" /></div>
                  <div className="form-group"><label className="form-label">Customer Name</label><input type="text" className="form-input" value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} data-testid="customer-input" /></div>
                </div>
                <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} data-testid="notes-input" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-order-btn">{editing ? 'Update' : 'Create Order'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionOrders;
